// Animation plein écran d'un jet : d20 qui roule (CSS 3D), valeur finale, modificateur, total vs DD, « Réussite ! / Échec / CRITIQUE ».
// Joue la file AppState.dice une par une (≈ 2,4 s chacune, toucher pour passer), puis shiftDice().
import { useEffect, useState } from "preact/hooks";
import type { DiceRollRecord } from "../../shared/types";
import { shiftDice, useAppState, type DiceShow } from "../store";
import "./DiceOverlay.css";

// Étapes d'un jet : roule → fige → calcul → DD → verdict (tenu avant le jet suivant).
const STAGE = { roule: 0, fige: 1, calcul: 2, dd: 3, verdict: 4 } as const;
const STAGE_MS = [900, 260, 260, 260] as const;
const VERDICT_HOLD_MS = 760;
const REDUCED_HOLD_MS = 1800;
const FLICKER_MS = 70;
const SPARKS = 14;

type Tone = "succes" | "echec" | "critique" | "echec-critique";
interface Verdict { text: string; tone: Tone }
interface Cursor { index: number; stage: number }

export function DiceOverlay() {
  const { dice } = useAppState();
  const current = dice[0];
  if (!current) return null;
  return <DiceShowPlayer key={current.id} show={current} />;
}

function DiceShowPlayer({ show }: { show: DiceShow }) {
  const [reduced] = useState(prefersReducedMotion);
  const [cursor, setCursor] = useState<Cursor>(() => ({ index: 0, stage: reduced ? STAGE.verdict : STAGE.roule }));
  const [flick, setFlick] = useState(0);
  const { index, stage } = cursor;
  const rec: DiceRollRecord | undefined = show.rolls[index];
  const last = index >= show.rolls.length - 1;

  const advance = () => {
    if (last) shiftDice();
    else setCursor({ index: index + 1, stage: reduced ? STAGE.verdict : STAGE.roule });
  };

  // Chiffres qui défilent pendant le roulement.
  useEffect(() => {
    if (stage !== STAGE.roule) return;
    const t = window.setInterval(() => setFlick((f) => f + 1), FLICKER_MS);
    return () => window.clearInterval(t);
  }, [index, stage]);

  // Étape suivante, puis jet suivant (ou fermeture). Un show sans dés est ignoré.
  useEffect(() => {
    if (!rec) {
      shiftDice();
      return;
    }
    const delay = stage < STAGE.verdict ? STAGE_MS[stage] : reduced ? REDUCED_HOLD_MS : VERDICT_HOLD_MS;
    const t = window.setTimeout(() => {
      if (stage < STAGE.verdict) setCursor({ index, stage: stage + 1 });
      else advance();
    }, delay);
    return () => window.clearTimeout(t);
  }, [index, stage]);

  // Secousse brève sur critique, si l'appareil la permet.
  useEffect(() => {
    if (stage === STAGE.verdict && rec?.crit) navigator.vibrate?.(rec.crit === "succes" ? [30, 60, 30] : 120);
  }, [index, stage]);

  if (!rec) return null;

  const hasDice = rec.rolls.length > 0;
  const sides = sidesOf(rec.notation) ?? 6;
  const isD20 = hasDice && sides === 20;
  const pair = isD20 && rec.mode !== "normal" && rec.rolls.length === 2;
  const keptIndex = pair && rec.rolls[0] !== rec.kept ? 1 : 0;
  const verdict = verdictOf(rec, isD20);
  const inVerdict = stage >= STAGE.verdict;
  const crack = inVerdict && verdict?.tone === "echec-critique";
  const burst = inVerdict && verdict?.tone === "critique";
  const size = isD20 ? (pair ? "m" : "xl") : rec.rolls.length > 3 ? "xs" : "s";
  const title = [show.actorName, rec.label].filter(Boolean).join(" — ");

  const onTap = () => {
    if (stage < STAGE.verdict) setCursor({ index, stage: STAGE.verdict });
    else advance();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onTap();
    }
  };

  return (
    <div class="dice-overlay" role="dialog" aria-modal="true" aria-label={title} tabIndex={0} onClick={onTap} onKeyDown={onKey}>
      <p class="dice-overlay__title">
        {show.actorName && (
          <>
            <span class="dice-overlay__actor">{show.actorName}</span>
            <span class="dice-overlay__sep"> — </span>
          </>
        )}
        <span class="dice-overlay__label">{rec.label}</span>
      </p>
      <div class="dice-overlay__meta">
        {pair && (
          <span class="chip chip-active">
            {rec.mode === "avantage" ? "Avantage : on garde le meilleur" : "Désavantage : on garde le pire"}
          </span>
        )}
        {show.rolls.length > 1 && <span class="dice-overlay__count">{index + 1} / {show.rolls.length}</span>}
      </div>
      <div class={`dice-overlay__dice${inVerdict && verdict ? ` dice-overlay__dice--${verdict.tone}` : ""}`}>
        {rec.rolls.map((value, i) => (
          <Die
            key={i}
            value={stage >= STAGE.fige ? value : faceAt(flick, sides, i)}
            size={size}
            rolling={stage === STAGE.roule}
            landed={stage >= STAGE.fige}
            discarded={pair && i !== keptIndex && stage >= STAGE.fige}
            crack={crack && i === keptIndex}
          />
        ))}
        {burst && <Burst />}
      </div>
      {stage >= STAGE.calcul && <p class="dice-overlay__equation">{equationOf(rec, isD20)}</p>}
      {stage >= STAGE.dd && rec.dc != null && <p class="dice-overlay__dc">DD {rec.dc}</p>}
      {inVerdict &&
        (verdict ? (
          <p class={`dice-overlay__verdict dice-overlay__verdict--${verdict.tone}`} role="status">{verdict.text}</p>
        ) : (
          <p class="dice-overlay__total" role="status">
            <span class="dice-overlay__total-label">Total</span>
            {rec.total}
          </p>
        ))}
      <p class="dice-overlay__hint">{stage < STAGE.verdict ? "Touche pour passer" : "Touchez pour continuer"}</p>
    </div>
  );
}

interface DieProps {
  value: number;
  size: "xl" | "m" | "s" | "xs";
  rolling: boolean;
  landed: boolean;
  discarded: boolean;
  crack: boolean;
}

/** Dé facetté en CSS 3D : une face avant (valeur) et une face arrière, qui roulent ensemble. */
function Die(p: DieProps) {
  const cls = ["dice-overlay__die", `dice-overlay__die--${p.size}`];
  if (p.rolling) cls.push("is-rolling");
  if (p.landed) cls.push("is-landed");
  if (p.discarded) cls.push("is-discarded");
  return (
    <div class={cls.join(" ")} aria-hidden="true">
      <div class="dice-overlay__face">
        <Facets />
        <span class="dice-overlay__value">{p.value}</span>
      </div>
      <div class="dice-overlay__face dice-overlay__face--back">
        <Facets />
      </div>
      {p.crack && (
        <svg class="dice-overlay__crack" viewBox="0 0 100 100">
          <polyline points="50,4 56,26 44,40 60,58 42,74 52,96" />
        </svg>
      )}
    </div>
  );
}

/** Six facettes autour du centre et contour doré (la forme d'un d20 vu de face). */
function Facets() {
  return (
    <svg class="dice-overlay__facets" viewBox="0 0 200 200" aria-hidden="true">
      <polygon class="dice-overlay__facet dice-overlay__facet--1" points="100,8 182,54 100,100" />
      <polygon class="dice-overlay__facet dice-overlay__facet--2" points="182,54 182,146 100,100" />
      <polygon class="dice-overlay__facet dice-overlay__facet--3" points="182,146 100,192 100,100" />
      <polygon class="dice-overlay__facet dice-overlay__facet--4" points="100,192 18,146 100,100" />
      <polygon class="dice-overlay__facet dice-overlay__facet--5" points="18,146 18,54 100,100" />
      <polygon class="dice-overlay__facet dice-overlay__facet--6" points="18,54 100,8 100,100" />
      <polygon class="dice-overlay__outline" points="100,8 182,54 182,146 100,192 18,146 18,54" />
    </svg>
  );
}

/** Explosion de braises pour un 20 naturel. */
function Burst() {
  return (
    <div class="dice-overlay__burst" aria-hidden="true">
      <span class="dice-overlay__flash" />
      {Array.from({ length: SPARKS }, (_, i) => (
        <span
          key={i}
          class="dice-overlay__spark"
          style={`--a:${(i * 360) / SPARKS}deg;--d:${96 + (i % 3) * 34}px`}
        />
      ))}
    </div>
  );
}

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Nombre de faces lu dans la notation : « 1d20+5 » → 20, « 2d6 » → 6. */
function sidesOf(notation: string): number | null {
  const m = /d(\d+)/i.exec(notation);
  return m ? Number(m[1]) : null;
}

/** Valeur affichée pendant le roulement : défilement déterministe, sans hasard. */
function faceAt(flick: number, sides: number, i: number): number {
  return ((flick * 7 + i * 5 + 3) % sides) + 1;
}

function verdictOf(rec: DiceRollRecord, isD20: boolean): Verdict | null {
  if (isD20 && rec.crit === "succes") return { text: "CRITIQUE !", tone: "critique" };
  if (isD20 && rec.crit === "echec") return { text: "ÉCHEC CRITIQUE", tone: "echec-critique" };
  if (rec.dc == null) return null;
  const success = rec.success ?? (rec.total >= rec.dc);
  return success ? { text: "RÉUSSITE", tone: "succes" } : { text: "ÉCHEC", tone: "echec" };
}

/** « 14 + 5 = 19 », « 3 + 5 − 1 = 7 » ; sans dé, le total seul. */
function equationOf(rec: DiceRollRecord, isD20: boolean): string {
  const parts = isD20 ? [rec.kept] : rec.rolls;
  if (parts.length === 0) return String(rec.total);
  const mod = rec.modifier === 0 ? "" : ` ${rec.modifier < 0 ? "−" : "+"} ${Math.abs(rec.modifier)}`;
  return `${parts.join(" + ")}${mod} = ${rec.total}`;
}
